import { useState, useEffect, useCallback } from 'react';
import { User, AuthState } from '../types/auth';
import {
  login as apiLogin,
  signup as apiSignup,
  loginWithMetaMask,
  saveToken,
  clearToken,
  getStoredToken,
} from '../services/authService';

function parseJwtPayload(token: string): { userId?: string; email?: string; exp?: number; isAdmin?: boolean; role?: string } | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1]));
    return payload;
  } catch {
    return null;
  }
}

function isTokenExpired(token: string): boolean {
  const payload = parseJwtPayload(token);
  if (!payload?.exp) return true;
  return Date.now() / 1000 > payload.exp;
}

const METAMASK_SIGN_MESSAGE = 'Sign this message to authenticate with SmartPredictions';

export function useAuth() {
  const [state, setState] = useState<AuthState>(() => {
    const token = getStoredToken();
    if (token && !isTokenExpired(token)) {
      const payload = parseJwtPayload(token);
      const user: User = {
        id: payload?.userId || '',
        email: payload?.email || null,
        walletAddress: null,
        preferredLanguage: 'en',
        createdAt: '',
        isAdmin: payload?.isAdmin === true || payload?.role === 'admin',
      };
      return { user, token, isAuthenticated: true, isLoading: false };
    }
    if (token) clearToken();
    return { user: null, token: null, isAuthenticated: false, isLoading: false };
  });

  // Listen for forced logout from API 401 responses
  useEffect(() => {
    const handler = () => {
      setState({ user: null, token: null, isAuthenticated: false, isLoading: false });
    };
    window.addEventListener('auth:logout', handler);
    return () => window.removeEventListener('auth:logout', handler);
  }, []);

  const setAuth = useCallback((token: string, user: User) => {
    saveToken(token);
    const payload = parseJwtPayload(token);
    const enrichedUser: User = {
      ...user,
      isAdmin: user.isAdmin ?? (payload?.isAdmin === true || payload?.role === 'admin'),
    };
    setState({ user: enrichedUser, token, isAuthenticated: true, isLoading: false });
  }, []);

  const logout = useCallback(() => {
    clearToken();
    setState({ user: null, token: null, isAuthenticated: false, isLoading: false });
  }, []);

  const loginWithEmail = useCallback(
    async (email: string, password: string) => {
      setState(s => ({ ...s, isLoading: true }));
      try {
        const res = await apiLogin({ email, password });
        setAuth(res.token, res.user);
      } finally {
        setState(s => ({ ...s, isLoading: false }));
      }
    },
    [setAuth],
  );

  const signupWithEmail = useCallback(
    async (email: string, password: string) => {
      setState(s => ({ ...s, isLoading: true }));
      try {
        const res = await apiSignup({ email, password });
        setAuth(res.token, res.user);
      } finally {
        setState(s => ({ ...s, isLoading: false }));
      }
    },
    [setAuth],
  );

  const loginWithWallet = useCallback(async () => {
    const eth = (window as any).ethereum;
    if (!eth) throw new Error('MetaMask not installed. Please install MetaMask.');
    setState(s => ({ ...s, isLoading: true }));
    try {
      const accounts: string[] = await eth.request({ method: 'eth_requestAccounts' });
      if (!accounts.length) throw new Error('No accounts found in MetaMask.');
      const address = accounts[0];
      const message = `${METAMASK_SIGN_MESSAGE}\n\nTimestamp: ${Date.now()}`;
      const signature: string = await eth.request({
        method: 'personal_sign',
        params: [message, address],
      });
      const res = await loginWithMetaMask({ address, signature, message });
      setAuth(res.token, res.user);
    } finally {
      setState(s => ({ ...s, isLoading: false }));
    }
  }, [setAuth]);

  return {
    ...state,
    loginWithEmail,
    signupWithEmail,
    loginWithWallet,
    logout,
  };
}
